import { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Download, Droplets, Loader2, Lock, LogOut, Phone, RefreshCw, Tags } from "lucide-react";
import Logo from "../components/Logo";
import AdminPricingPanel from "../components/AdminPricingPanel";
import { API, DEFAULT_PRICING, formatLei } from "../lib/api";

const CASING_LABELS = Object.fromEntries(
  DEFAULT_PRICING.casings.map((c) => [c.id, c.label]),
);

const STATUSES = [
  { id: "nou", label: "Nou" },
  { id: "sunat", label: "Sunat" },
  { id: "programat", label: "Programat" },
];
const STATUS_LABELS = Object.fromEntries(STATUSES.map((s) => [s.id, s.label]));
const STATUS_STYLE = {
  nou: "border-amber-400/40 bg-amber-400/10 text-amber-300",
  sunat: "border-emerald-400/40 bg-emerald-400/10 text-emerald-300",
  programat: "border-sky-400/40 bg-sky-400/10 text-sky-300",
};

const FILTERS = [
  { id: "toate", label: "Toate" },
  ...STATUSES,
];

function GoogleIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true">
      <path fill="#4285F4" d="M23.5 12.3c0-.9-.1-1.5-.3-2.2H12v4.1h6.5c-.1 1.1-.8 2.7-2.4 3.8l-.1.1 3.6 2.8c2.1-2 3.9-4.9 3.9-8.6z" />
      <path fill="#34A853" d="M12 24c3.2 0 5.9-1.1 7.6-2.9l-3.6-2.8c-1 .7-2.3 1.2-4 1.2-3.1 0-5.7-2-6.6-4.8l-.1.1-3.7 2.9C3.6 21.3 7.5 24 12 24z" />
      <path fill="#FBBC05" d="M5.4 14.7c-.2-.7-.4-1.5-.4-2.4s.1-1.7.4-2.4l-.1-.1-3.8-2.9C.6 8.5.3 10.2.3 12s.3 3.5 1.2 5l3.9-2.3z" />
      <path fill="#EA4335" d="M12 4.7c2.2 0 3.7.9 4.5 1.7l3.3-3.2C17.9 1.3 15.2 0 12 0 7.5 0 3.6 2.7 1.5 6.6l3.9 3c.9-2.8 3.5-4.9 6.6-4.9z" />
    </svg>
  );
}

function formatApiErrorDetail(detail) {
  if (detail == null) return "Ceva n-a mers bine. Încearcă din nou.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail
      .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
      .filter(Boolean)
      .join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

function formatDateTime(iso) {
  try {
    return new Date(iso).toLocaleString("ro-RO", { dateStyle: "medium", timeStyle: "short" });
  } catch (e) {
    return iso;
  }
}

function csvCell(value) {
  return `"${String(value).replace(/"/g, '""')}"`;
}

function downloadCsv(quotes) {
  const header = [
    "Data", "Nume", "Telefon", "Localitate", "Adâncime (m)",
    "Țeavă", "Estimare min (lei)", "Estimare max (lei)", "Status", "Mesaj",
  ];
  const rows = quotes.map((q) => [
    formatDateTime(q.created_at),
    q.name,
    `="${q.phone}"`,
    q.locality,
    q.depth_m,
    CASING_LABELS[q.casing] || q.casing,
    q.estimate_min,
    q.estimate_max,
    STATUS_LABELS[q.status || "nou"],
    q.message || "",
  ]);
  const csv =
    "\ufeff" + [header, ...rows].map((r) => r.map(csvCell).join(";")).join("\r\n");
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `cereri-putul-tau-${new Date().toISOString().slice(0, 10)}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

function StatusSelect({ quote, onChange }) {
  const current = quote.status || "nou";
  return (
    <select
      value={current}
      data-testid={`admin-quote-status-${quote.id}`}
      onChange={(e) => onChange(e.target.value)}
      aria-label="Status cerere"
      className={`cursor-pointer appearance-none rounded-full border px-3 py-1.5 text-[11px] font-bold outline-none transition-colors ${STATUS_STYLE[current]}`}
    >
      {STATUSES.map((s) => (
        <option key={s.id} value={s.id} className="bg-hydro-navy text-white">
          {s.label}
        </option>
      ))}
    </select>
  );
}

export default function AdminPage() {
  const [user, setUser] = useState(undefined);
  const [tab, setTab] = useState("quotes");
  const [quotes, setQuotes] = useState([]);
  const [loadingQuotes, setLoadingQuotes] = useState(false);
  const [filter, setFilter] = useState("toate");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loginError, setLoginError] = useState("");
  const [loggingIn, setLoggingIn] = useState(false);
  const hasProcessedCallback = useRef(false);

  const loadQuotes = useCallback(async () => {
    setLoadingQuotes(true);
    try {
      const { data } = await axios.get(`${API}/quotes`, { withCredentials: true });
      setQuotes(data.quotes || []);
    } catch (e) {
      if (e.response?.status === 401) {
        setUser(null);
      } else {
        toast.error("Nu am putut încărca cererile.");
      }
    } finally {
      setLoadingQuotes(false);
    }
  }, []);

  useEffect(() => {
    if (hasProcessedCallback.current) return;
    hasProcessedCallback.current = true;

    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const hash = window.location.hash || "";
    if (hash.includes("session_id=")) {
      const sessionId = new URLSearchParams(hash.replace(/^#/, "")).get("session_id");
      window.history.replaceState({}, "", window.location.pathname);
      if (!sessionId) {
        setUser(null);
        return;
      }
      axios
        .post(`${API}/auth/google-session`, { session_id: sessionId }, { withCredentials: true })
        .then((r) => {
          setUser(r.data.user);
          loadQuotes();
        })
        .catch((err) => {
          toast.error("Autentificarea cu Google a eșuat.", {
            description: formatApiErrorDetail(err.response?.data?.detail),
          });
          setUser(null);
        });
      // dacă venim din callback, sărim peste verificarea /auth/me — sesiunea se stabilește chiar acum
      return;
    }

    axios
      .get(`${API}/auth/me`, { withCredentials: true })
      .then((r) => {
        setUser(r.data);
        loadQuotes();
      })
      .catch(() => setUser(null));
  }, [loadQuotes]);

  const loginWithGoogle = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(
      window.location.origin + "/admin",
    )}`;
  };

  const login = async (e) => {
    e.preventDefault();
    setLoginError("");
    setLoggingIn(true);
    try {
      const { data } = await axios.post(`${API}/admin/login`, { email, password }, { withCredentials: true });
      setUser(data.user);
      loadQuotes();
    } catch (err) {
      setLoginError(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setLoggingIn(false);
    }
  };

  const logout = async () => {
    await axios.post(`${API}/auth/logout`, {}, { withCredentials: true }).catch(() => {});
    setUser(null);
    setQuotes([]);
  };

  const changeStatus = async (quote, status) => {
    setQuotes((prev) => prev.map((x) => (x.id === quote.id ? { ...x, status } : x)));
    try {
      await axios.patch(
        `${API}/quotes/${quote.id}/status`,
        { status },
        { withCredentials: true },
      );
    } catch (err) {
      toast.error("Nu am putut actualiza statusul.");
      loadQuotes();
    }
  };

  if (user === undefined) {
    return (
      <div className="min-h-screen grid place-items-center bg-hydro-navy text-white/70 font-body">
        <div className="flex items-center gap-3 text-sm">
          <Loader2 size={18} className="animate-spin" />
          Se încarcă...
        </div>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="min-h-screen grid place-items-center bg-hydro-navy px-5 font-body">
        <div className="w-full max-w-sm rounded-3xl border border-white/10 bg-white/[0.04] p-8">
          <div className="flex justify-center">
            <Logo />
          </div>
          <div className="mt-8 text-center">
            <div className="mx-auto grid h-12 w-12 place-items-center rounded-full bg-hydro-cyan/10 text-hydro-cyan">
              <Lock size={20} />
            </div>
            <h1 className="mt-4 font-display text-2xl font-extrabold text-white">Panou de administrare</h1>
            <p className="mt-1 text-sm text-white/50">Intră cu contul tău de administrator.</p>
          </div>

          <button
            type="button"
            onClick={loginWithGoogle}
            data-testid="admin-google-signin"
            className="mt-7 flex h-12 w-full items-center justify-center gap-3 rounded-full bg-white text-sm font-bold text-slate-800 transition-colors hover:bg-slate-100"
          >
            <GoogleIcon />
            Continuă cu Google
          </button>

          <div className="my-5 flex items-center gap-3">
            <span className="h-px flex-1 bg-white/10" />
            <span className="font-mono text-[10px] uppercase tracking-widest text-white/35">
              sau cu email și parolă
            </span>
            <span className="h-px flex-1 bg-white/10" />
          </div>

          <form onSubmit={login} className="space-y-4">
            <input
              type="email"
              required
              data-testid="admin-email-input"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Email administrator"
              className="h-12 w-full rounded-xl border border-white/15 bg-hydro-navy/60 px-4 text-sm text-white placeholder:text-white/35 outline-none focus:border-hydro-cyan"
            />
            <input
              type="password"
              required
              data-testid="admin-password-input"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Parolă"
              className="h-12 w-full rounded-xl border border-white/15 bg-hydro-navy/60 px-4 text-sm text-white placeholder:text-white/35 outline-none focus:border-hydro-cyan"
            />
            {loginError && (
              <p data-testid="admin-login-error" className="text-sm font-semibold text-red-400">
                {loginError}
              </p>
            )}
            <button
              type="submit"
              disabled={loggingIn}
              data-testid="admin-login-button"
              className="h-12 w-full rounded-full bg-hydro-cyan text-base font-extrabold text-hydro-navy transition-colors hover:bg-white disabled:opacity-60"
            >
              {loggingIn ? "Se conectează..." : "Intră în panou"}
            </button>
          </form>
          <a href="/" data-testid="admin-back-home-link" className="mt-6 block text-center text-xs text-white/40 hover:text-white/70">
            ← Înapoi la site
          </a>
        </div>
      </div>
    );
  }

  const visible = filter === "toate" ? quotes : quotes.filter((q) => (q.status || "nou") === filter);

  return (
    <div className="min-h-screen bg-hydro-navy font-body">
      <header className="border-b border-white/10 bg-hydro-navy/80 backdrop-blur-xl sticky top-0 z-40">
        <div className="mx-auto flex max-w-6xl flex-wrap items-center gap-4 px-5 py-4 sm:px-8">
          <Logo compact />
          <div className="mr-auto">
            <h1 className="font-display text-lg font-extrabold leading-tight text-white">Panou de administrare</h1>
            <p data-testid="admin-quotes-count" className="font-mono text-[11px] uppercase tracking-widest text-white/40">
              {quotes.length} cereri · {user.email}
            </p>
          </div>
          <button
            type="button"
            onClick={() => loadQuotes()}
            data-testid="admin-refresh-button"
            className="inline-flex items-center gap-2 rounded-full border border-white/15 px-4 py-2 text-xs font-bold text-white/70 transition-colors hover:border-hydro-cyan hover:text-hydro-cyan"
          >
            <RefreshCw size={14} className={loadingQuotes ? "animate-spin" : ""} />
            Reîmprospătează
          </button>
          {tab === "quotes" && (
            <button
              type="button"
              onClick={() => downloadCsv(quotes)}
              disabled={quotes.length === 0}
              data-testid="admin-download-csv"
              className="inline-flex items-center gap-2 rounded-full bg-hydro-cyan px-4 py-2 text-xs font-extrabold text-hydro-navy transition-colors hover:bg-white disabled:opacity-40"
            >
              <Download size={14} />
              Descarcă CSV
            </button>
          )}
          <button
            type="button"
            onClick={logout}
            data-testid="admin-logout-button"
            className="inline-flex items-center gap-2 rounded-full border border-white/15 px-4 py-2 text-xs font-bold text-white/70 transition-colors hover:border-red-400 hover:text-red-400"
          >
            <LogOut size={14} />
            Ieșire
          </button>
        </div>
        <div className="mx-auto flex max-w-6xl gap-2 px-5 pb-4 sm:px-8">
          <button
            type="button"
            data-testid="admin-tab-quotes"
            onClick={() => setTab("quotes")}
            className={`rounded-full px-5 py-2 text-sm font-bold transition-colors ${
              tab === "quotes" ? "bg-white/10 text-white" : "text-white/45 hover:text-white"
            }`}
          >
            Cererile
          </button>
          <button
            type="button"
            data-testid="admin-tab-pricing"
            onClick={() => setTab("pricing")}
            className={`inline-flex items-center gap-2 rounded-full px-5 py-2 text-sm font-bold transition-colors ${
              tab === "pricing" ? "bg-white/10 text-white" : "text-white/45 hover:text-white"
            }`}
          >
            <Tags size={14} />
            Prețuri
          </button>
        </div>
      </header>

      <main className="mx-auto max-w-6xl px-5 py-10 sm:px-8">
        {tab === "pricing" ? (
          <AdminPricingPanel />
        ) : loadingQuotes && quotes.length === 0 ? (
          <div className="flex items-center justify-center gap-3 py-24 text-sm text-white/50">
            <Loader2 size={18} className="animate-spin" />
            Se încarcă cererile...
          </div>
        ) : quotes.length === 0 ? (
          <div className="rounded-3xl border border-dashed border-white/15 p-14 text-center" data-testid="admin-empty-state">
            <div className="mx-auto grid h-14 w-14 place-items-center rounded-full bg-hydro-cyan/10 text-hydro-cyan">
              <Droplets size={24} />
            </div>
            <p className="mt-5 font-display text-xl font-bold text-white">Nu ai primit încă nicio cerere.</p>
            <p className="mt-2 text-sm text-white/50">
              De îndată ce un client trimite formularul de ofertă de pe site, cererea apare aici.
            </p>
          </div>
        ) : (
          <>
            <div className="flex flex-wrap gap-2" data-testid="admin-status-filters">
              {FILTERS.map((f) => {
                const n =
                  f.id === "toate"
                    ? quotes.length
                    : quotes.filter((q) => (q.status || "nou") === f.id).length;
                const active = filter === f.id;
                return (
                  <button
                    key={f.id}
                    type="button"
                    data-testid={`admin-filter-${f.id}`}
                    onClick={() => setFilter(f.id)}
                    className={`rounded-full border px-4 py-2 text-xs font-bold transition-colors ${
                      active
                        ? "border-hydro-cyan bg-hydro-cyan text-hydro-navy"
                        : "border-white/15 text-white/60 hover:border-white/40 hover:text-white"
                    }`}
                  >
                    {f.label} · {n}
                  </button>
                );
              })}
            </div>

            {visible.length === 0 ? (
              <div className="mt-8 rounded-3xl border border-dashed border-white/15 p-10 text-center text-sm text-white/50" data-testid="admin-filter-empty-state">
                Nicio cerere cu statusul „{STATUS_LABELS[filter]}".
              </div>
            ) : (
              <>
                {/* desktop table */}
                <div className="mt-8 hidden overflow-x-auto rounded-3xl border border-white/10 md:block" data-testid="admin-quotes-table">
                  <table className="w-full text-left text-sm">
                    <thead>
                      <tr className="border-b border-white/10 font-mono text-[11px] uppercase tracking-widest text-white/40">
                        <th className="px-5 py-4 font-medium">Data</th>
                        <th className="px-5 py-4 font-medium">Client</th>
                        <th className="px-5 py-4 font-medium">Localitate</th>
                        <th className="px-5 py-4 font-medium">Adâncime</th>
                        <th className="px-5 py-4 font-medium">Estimare</th>
                        <th className="px-5 py-4 font-medium">Status</th>
                        <th className="px-5 py-4 font-medium">Mesaj</th>
                      </tr>
                    </thead>
                    <tbody>
                      {visible.map((q, i) => (
                        <tr
                          key={q.id || i}
                          data-testid={`admin-quote-row-${i}`}
                          className="border-b border-white/5 align-top transition-colors last:border-0 hover:bg-white/[0.03]"
                        >
                          <td className="whitespace-nowrap px-5 py-4 text-xs text-white/50">{formatDateTime(q.created_at)}</td>
                          <td className="px-5 py-4">
                            <span className="block font-bold text-white">{q.name}</span>
                            <a
                              href={`tel:${q.phone}`}
                              data-testid={`admin-quote-phone-${i}`}
                              className="mt-0.5 inline-flex items-center gap-1.5 text-xs font-semibold text-hydro-cyan hover:text-white"
                            >
                              <Phone size={11} />
                              {q.phone}
                            </a>
                          </td>
                          <td className="px-5 py-4 text-white/70">
                            <span className="block">{q.locality}</span>
                            <span className="mt-0.5 block text-[11px] text-white/40">
                              {q.depth_m} m · {CASING_LABELS[q.casing] || q.casing}
                            </span>
                          </td>
                          <td className="px-5 py-4 font-mono text-white/70">{q.depth_m} m</td>
                          <td className="whitespace-nowrap px-5 py-4 font-mono font-semibold text-white">
                            {formatLei(q.estimate_min)}–{formatLei(q.estimate_max)} lei
                          </td>
                          <td className="px-5 py-4">
                            <StatusSelect quote={q} onChange={(s) => changeStatus(q, s)} />
                          </td>
                          <td className="max-w-[200px] px-5 py-4 text-xs leading-relaxed text-white/50">{q.message || "—"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* mobile cards */}
                <div className="mt-8 space-y-4 md:hidden">
                  {visible.map((q, i) => (
                    <div
                      key={q.id || i}
                      data-testid={`admin-quote-card-${i}`}
                      className="rounded-2xl border border-white/10 bg-white/[0.04] p-5"
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div>
                          <p className="font-display text-lg font-bold text-white">{q.name}</p>
                          <a
                            href={`tel:${q.phone}`}
                            data-testid={`admin-quote-mobile-phone-${i}`}
                            className="mt-1 inline-flex items-center gap-1.5 text-sm font-bold text-hydro-cyan"
                          >
                            <Phone size={13} />
                            {q.phone}
                          </a>
                        </div>
                        <span className="shrink-0 font-mono text-[10px] text-white/40">{formatDateTime(q.created_at)}</span>
                      </div>
                      <div className="mt-4 grid grid-cols-2 gap-3 text-xs">
                        <div>
                          <span className="block text-white/40">Localitate</span>
                          <span className="mt-0.5 block font-semibold text-white/80">{q.locality}</span>
                        </div>
                        <div>
                          <span className="block text-white/40">Adâncime</span>
                          <span className="mt-0.5 block font-mono font-semibold text-white/80">{q.depth_m} m</span>
                        </div>
                        <div>
                          <span className="block text-white/40">Țeavă</span>
                          <span className="mt-0.5 block font-semibold text-white/80">{CASING_LABELS[q.casing] || q.casing}</span>
                        </div>
                        <div>
                          <span className="block text-white/40">Estimare</span>
                          <span className="mt-0.5 block font-mono font-semibold text-white/80">
                            {formatLei(q.estimate_min)}–{formatLei(q.estimate_max)} lei
                          </span>
                        </div>
                      </div>
                      {q.message && (
                        <p className="mt-4 rounded-xl bg-white/[0.04] p-3 text-xs leading-relaxed text-white/60">{q.message}</p>
                      )}
                      <div className="mt-4 flex items-center justify-between">
                        <span className="text-xs text-white/40">Status</span>
                        <StatusSelect quote={q} onChange={(s) => changeStatus(q, s)} />
                      </div>
                    </div>
                  ))}
                </div>
              </>
            )}
          </>
        )}
      </main>
    </div>
  );
}
